import React, { useState, useEffect } from 'react';
import { LogIn, UserPlus, Wrench, MailCheck, Mail, Loader2, AlertCircle, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as BASE } from '../lib/apiBase';

export default function LoginPage({ initialMode }) {
  const { t, lang, setLang, languages } = useT();
  const { login, refreshMe, forgotPassword, resetPassword } = useAuth();
  const [mode, setMode] = useState(initialMode === 'register' ? 'register' : 'login'); // login | register | sentMail | activating | activated | forgot | sentReset | resetting | reset
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [naam, setNaam] = useState('');
  const [garageNaam, setGarageNaam] = useState('');
  const [activationMsg, setActivationMsg] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');

  // Check URL voor activatie token of password reset token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const isReset = window.location.pathname.includes('/reset-password') || params.get('reset') === '1';
    if (!token) return;
    if (isReset) {
      setResetToken(token);
      setMode('resetting');
      window.history.replaceState({}, '', '/');
      return;
    }
    let active = true;
    Promise.resolve().then(() => active && setMode('activating'));
    axios.get(`${BASE}/api/auth/activate?token=${encodeURIComponent(token)}`)
      .then(r => {
        if (!active) return;
        localStorage.setItem('monteurcentraal_token', r.data.token);
        window.history.replaceState({}, '', '/');
        setActivationMsg(r.data.alreadyActive ? 'Account was al geactiveerd. Je bent ingelogd.' : 'Account succesvol geactiveerd! Welkom bij MonteurCentraal.');
        setMode('activated');
        return refreshMe();
      })
      .catch(err => {
        if (!active) return;
        setActivationMsg(err.response?.data?.detail || 'Activatie mislukt. Vraag een nieuwe activatiemail aan.');
        setMode('activated');
      });
    return () => { active = false; };
  }, [refreshMe]);

  const handleForgot = (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error(t('Vul je e-mail in')); return; }
    setBusy(true);
    forgotPassword(email.trim().toLowerCase())
      .then(() => {
        toast.success(t('Reset-mail verstuurd', 'Als dit e-mailadres bekend is, hebben we een reset-link gestuurd.'));
        setMode('sentReset');
      })
      .catch(err => toast.error(t('Versturen mislukt'), { description: err.response?.data?.detail || err.message }))
      .finally(() => setBusy(false));
  };

  const handleReset = (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error(t('Wachtwoord minimaal 6 tekens')); return; }
    if (newPwd !== newPwd2) { toast.error(t('Wachtwoorden komen niet overeen', 'Wachtwoorden komen niet overeen')); return; }
    setBusy(true);
    resetPassword(resetToken, newPwd)
      .then(() => {
        toast.success(t('Wachtwoord gewijzigd', 'Wachtwoord gewijzigd! Je bent ingelogd.'));
        setMode('reset');
        setTimeout(() => { window.location.href = '/'; }, 1200);
      })
      .catch(err => toast.error(t('Reset mislukt', 'Reset mislukt'), { description: err.response?.data?.detail || err.message }))
      .finally(() => setBusy(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setBusy(true);
    if (mode === 'login') {
      login(email.trim().toLowerCase(), password)
        .then(() => { toast.success(t('Welkom terug!')); })
        .catch(err => {
          const detail = err.response?.data?.detail || err.message;
          toast.error(t('Inloggen mislukt'), { description: detail });
          if (err.response?.status === 403 && /geactiveerd/i.test(detail)) {
            setMode('sentMail');
          }
        })
        .finally(() => setBusy(false));
      return;
    }
    if (!garageNaam.trim()) { toast.error(t('Garagenaam verplicht')); setBusy(false); return; }
    if (password.length < 6) { toast.error(t('Wachtwoord minimaal 6 tekens')); setBusy(false); return; }
    axios.post(`${BASE}/api/auth/register`, {
      email: email.trim().toLowerCase(), password, naam: naam.trim(), garageNaam: garageNaam.trim(),
    })
      .then(r => {
        if (r.data.emailSent) {
          toast.success(t('Account aangemaakt! Check je e-mail om te activeren.'));
        } else {
          toast.warning(t('Account aangemaakt, maar mail kon niet verstuurd worden. Probeer "Opnieuw versturen".'), { description: r.data.emailError });
        }
        setMode('sentMail');
      })
      .catch(err => {
        toast.error(t('Registratie mislukt'), { description: err.response?.data?.detail || err.message });
      })
      .finally(() => setBusy(false));
  };

  const handleResend = () => {
    if (!email) { toast.error(t('Vul je e-mail in')); return; }
    setBusy(true);
    axios.post(`${BASE}/api/auth/resend-activation`, { email: email.trim().toLowerCase() })
      .then(r => { toast.success(r.data.message || 'Activatiemail verstuurd. Check je inbox.'); })
      .catch(err => { toast.error(t('Opnieuw versturen mislukt'), { description: err.response?.data?.detail || err.message }); })
      .finally(() => setBusy(false));
  };

  const fillDemo = () => {
    // Demo-account is verwijderd in productie — deze functie wordt niet meer gebruikt.
    setMode('login');
  };

  return (
    <div className="login-wrap">
      <div className="login-box" data-testid="login-box" style={{ position: 'relative' }}>
        <div className="login-lang" data-testid="login-lang-switch">
          <Globe size={13} />
          <select
            data-testid="login-lang-select"
            value={lang}
            onChange={e => setLang(e.target.value)}
            aria-label="Taal / Language"
          >
            {(languages || []).map(l => (
              <option key={l.code} value={l.code}>{l.flag ? `${l.flag} ` : ''}{l.name}</option>
            ))}
          </select>
        </div>
        <div className="login-brand">
          <div className="brand-icon"><Wrench size={26} /></div>
          <h1>MonteurCentraal</h1>
          <p>{t('Garage management voor moderne werkplaatsen', 'Garage management voor moderne werkplaatsen')}</p>
        </div>

        {(mode === 'login' || mode === 'register') && (
          <div className="login-tabs">
            <button data-testid="tab-login" className={`tab ${mode === 'login' ? 'active' : ''}`} onClick={() => setMode('login')}>{t('Inloggen', 'Inloggen')}</button>
            <button data-testid="tab-register" className={`tab ${mode === 'register' ? 'active' : ''}`} onClick={() => setMode('register')}>{t('Registreren', 'Registreren')}</button>
          </div>
        )}

        {mode === 'activating' && (
          <div data-testid="activating-state" style={{ textAlign: 'center', padding: '24px 0' }}>
            <Loader2 size={32} className="spin" style={{ color: 'var(--cs-accent)' }} />
            <p style={{ marginTop: 12, color: '#64748b' }}>{t('Account activeren...')}</p>
          </div>
        )}

        {mode === 'activated' && (
          <div data-testid="activated-state" style={{ textAlign: 'center', padding: '12px 0 8px' }}>
            <MailCheck size={42} style={{ color: 'var(--cs-green)' }} />
            <h3 style={{ margin: '12px 0 6px', fontSize: 18 }}>{activationMsg.startsWith('Account') ? t('Geactiveerd!') : t('Helaas')}</h3>
            <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: 13.5 }}>{activationMsg}</p>
            <button className="primary" style={{ width: '100%' }} onClick={() => { window.location.href = '/'; }}>{t('Doorgaan naar app')}</button>
          </div>
        )}

        {mode === 'sentMail' && (
          <div data-testid="sent-mail-state" style={{ textAlign: 'center', padding: '8px 0' }}>
            <Mail size={40} style={{ color: 'var(--cs-accent)' }} />
            <h3 style={{ margin: '12px 0 6px', fontSize: 18 }}>{t('Check je inbox')}</h3>
            <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: 13.5, lineHeight: 1.6 }}>
              {t('We hebben een activatielink gestuurd naar')} <b>{email}</b>. {t('Klik op de knop in de mail om je account te activeren.')}
            </p>
            <div style={{ background: '#fef3c7', color: '#92400e', padding: '10px 12px', borderRadius: 8, fontSize: 12, marginBottom: 14, display: 'flex', gap: 8, alignItems: 'flex-start', textAlign: 'left' }}>
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span><b>{t('Geen mail?', 'Geen mail?')}</b> {t('Check je spam-map. De mail kan tot een minuut duren.', 'Check je spam-map. De mail kan tot een minuut duren.')}</span>
            </div>
            <button data-testid="btn-resend" className="primary" style={{ width: '100%', marginBottom: 8 }} onClick={handleResend} disabled={busy}>
              {busy ? <Loader2 size={14} className="spin" /> : <Mail size={14} />} {t('Opnieuw versturen', 'Opnieuw versturen')}
            </button>
            <button className="link" onClick={() => setMode('login')}>← {t('Terug naar inloggen')}</button>
          </div>
        )}

        {(mode === 'login' || mode === 'register') && (
          <form onSubmit={handleSubmit} className="login-form">
            {mode === 'register' && (
              <>
                <label>{t('Naam')}</label>
                <input data-testid="input-naam" type="text" value={naam} onChange={e => setNaam(e.target.value)} placeholder={t('Voor- en achternaam')} />
                <label>{t('Garagenaam')}</label>
                <input data-testid="input-garagenaam" type="text" value={garageNaam} onChange={e => setGarageNaam(e.target.value)} placeholder={t('bv. Garage Janssen')} required />
              </>
            )}
            <label>{t('E-mail')}</label>
            <input data-testid="input-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            <label>{t('Wachtwoord')}</label>
            <input data-testid="input-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
            <button data-testid="btn-submit" type="submit" className="primary" disabled={busy}>
              {busy ? <Loader2 size={14} className="spin" /> : (mode === 'login' ? <LogIn size={14} /> : <UserPlus size={14} />)}
              {mode === 'login' ? ` ${t('Inloggen', 'Inloggen')}` : ` ${t('Account aanmaken', 'Account aanmaken')}`}
            </button>
            {mode === 'login' && (
              <button type="button" data-testid="btn-forgot" className="link" style={{ marginTop: 8 }} onClick={() => setMode('forgot')}>
                {t('Wachtwoord vergeten?', 'Wachtwoord vergeten?')}
              </button>
            )}
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgot} className="login-form" data-testid="forgot-form">
            <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>{t('Wachtwoord resetten', 'Wachtwoord resetten')}</h3>
            <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>
              {t('Vul je e-mailadres in. We sturen je een link om een nieuw wachtwoord in te stellen.', 'Vul je e-mailadres in. We sturen je een link om een nieuw wachtwoord in te stellen.')}
            </p>
            <label>{t('E-mail')}</label>
            <input data-testid="input-forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            <button data-testid="btn-forgot-submit" type="submit" className="primary" disabled={busy}>
              {busy ? <Loader2 size={14} className="spin" /> : <Mail size={14} />} {t('Reset-link versturen', 'Reset-link versturen')}
            </button>
            <button type="button" className="link" style={{ marginTop: 8 }} onClick={() => setMode('login')}>← {t('Terug naar inloggen', 'Terug naar inloggen')}</button>
          </form>
        )}

        {mode === 'sentReset' && (
          <div data-testid="sent-reset-state" style={{ textAlign: 'center', padding: '8px 0' }}>
            <MailCheck size={42} style={{ color: 'var(--cs-green)' }} />
            <h3 style={{ margin: '12px 0 6px', fontSize: 18 }}>{t('Check je inbox', 'Check je inbox')}</h3>
            <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: 13.5, lineHeight: 1.6 }}>
              {t('Als', 'Als')} <b>{email}</b> {t('bekend is, hebben we een reset-link verstuurd. De link is 2 uur geldig.', 'bekend is, hebben we een reset-link verstuurd. De link is 2 uur geldig.')}
            </p>
            <button className="link" onClick={() => setMode('login')}>← {t('Terug naar inloggen', 'Terug naar inloggen')}</button>
          </div>
        )}

        {mode === 'resetting' && (
          <form onSubmit={handleReset} className="login-form" data-testid="reset-form">
            <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>{t('Nieuw wachtwoord instellen', 'Nieuw wachtwoord instellen')}</h3>
            <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>
              {t('Kies een sterk wachtwoord van minimaal 6 tekens.', 'Kies een sterk wachtwoord van minimaal 6 tekens.')}
            </p>
            <label>{t('Nieuw wachtwoord', 'Nieuw wachtwoord')}</label>
            <input data-testid="input-new-pwd" type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required minLength={6} autoComplete="new-password" />
            <label>{t('Wachtwoord herhalen', 'Wachtwoord herhalen')}</label>
            <input data-testid="input-new-pwd2" type="password" value={newPwd2} onChange={e => setNewPwd2(e.target.value)} required minLength={6} autoComplete="new-password" />
            <button data-testid="btn-reset-submit" type="submit" className="primary" disabled={busy}>
              {busy ? <Loader2 size={14} className="spin" /> : <LogIn size={14} />} {t('Wachtwoord opslaan', 'Wachtwoord opslaan')}
            </button>
          </form>
        )}

        {mode === 'reset' && (
          <div data-testid="reset-success" style={{ textAlign: 'center', padding: '12px 0 8px' }}>
            <MailCheck size={42} style={{ color: 'var(--cs-green)' }} />
            <h3 style={{ margin: '12px 0 6px', fontSize: 18 }}>{t('Wachtwoord gewijzigd!', 'Wachtwoord gewijzigd!')}</h3>
            <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: 13.5 }}>{t('Je bent ingelogd, een moment...', 'Je bent ingelogd, een moment...')}</p>
          </div>
        )}

        {(mode === 'login' || mode === 'register') && (
          <div className="login-footer">
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {mode === 'login' ? t('Nog geen account? Klik op "Account aanmaken" boven.', 'Nog geen account? Klik op "Account aanmaken" boven.') : t('Heb je al een account? Klik op "Inloggen" boven.', 'Heb je al een account? Klik op "Inloggen" boven.')}
            </span>
            <div style={{ marginTop: 10 }}>
              <a href="/pricing" data-testid="link-pricing" style={{ color: '#2563eb', fontSize: 12, textDecoration: 'none' }}>
                {t('Nog geen licentie? Bekijk onze pakketten', 'Nog geen licentie? Bekijk onze pakketten')} →
              </a>
            </div>
            <div style={{ marginTop: 12, fontSize: 11.5, color: '#94a3b8' }}>
              <a href="/voorwaarden" data-testid="link-voorwaarden" style={{ color: '#94a3b8', textDecoration: 'none' }}>{t('Algemene voorwaarden', 'Algemene voorwaarden')}</a>
              <span style={{ margin: '0 8px' }}>·</span>
              <a href="/privacy" data-testid="link-privacy" style={{ color: '#94a3b8', textDecoration: 'none' }}>{t('Privacyverklaring', 'Privacyverklaring')}</a>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .login-wrap { position: fixed; inset: 0; background: linear-gradient(135deg, #e0e7ff 0%, #f3f6fb 50%, #dbeafe 100%); display: flex; align-items: center; justify-content: center; padding: 20px; z-index: 999; }
        .login-box { background: #fff; border-radius: 16px; padding: 38px 40px; width: 440px; max-width: 96vw; box-shadow: 0 20px 60px rgba(15,23,42,0.12); }
        .login-brand { text-align: center; margin-bottom: 26px; }
        .brand-icon { display: inline-flex; width: 56px; height: 56px; border-radius: 14px; background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%); align-items: center; justify-content: center; color: white; box-shadow: 0 8px 20px rgba(37,99,235,0.35); margin-bottom: 14px; }
        .login-brand h1 { margin: 0; font-size: 26px; font-weight: 800; letter-spacing: -0.5px; color: #0f172a; }
        .login-brand p { margin: 4px 0 0; color: #64748b; font-size: 13px; }
        .login-tabs { display: flex; background: #f1f5f9; padding: 4px; border-radius: 8px; margin-bottom: 20px; }
        .login-tabs .tab { flex: 1; padding: 8px; border: none; background: transparent; border-radius: 6px; font-size: 13.5px; font-weight: 500; cursor: pointer; color: #64748b; }
        .login-tabs .tab.active { background: #fff; color: #0f172a; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
        .login-form { display: flex; flex-direction: column; gap: 4px; }
        .login-form label { font-size: 11.5px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 8px; }
        .login-form input { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 7px; font-size: 14px; font-family: inherit; transition: border-color 120ms, box-shadow 120ms; }
        .login-form input:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }
        .primary { padding: 10px; background: #2563eb; color: #fff; border: none; border-radius: 7px; font-weight: 600; font-size: 14px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; transition: background 120ms; }
        .login-form button.primary { margin-top: 18px; }
        .primary:hover:not(:disabled) { background: #1d4ed8; }
        .primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .login-footer { margin-top: 18px; text-align: center; padding-top: 16px; border-top: 1px solid #f1f5f9; }
        .link { background: none; border: none; color: #2563eb; cursor: pointer; font-size: 12.5px; font-weight: 500; }
        .link:hover { text-decoration: underline; }
        .spin { animation: spin 800ms linear infinite; }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
